import { useState, useEffect, useMemo } from "react";
import DropdownNoLabel from "../../DropDown/DropdownNoLabel";
import RequestedIconCheck from "../../../Assets/images/requested.svg";
import RequestedRoomIconCheck from "../../../Assets/images/room.svg";
import { useGlobalContext } from "../../../Context/GlobalContext";
import styles from "../../RightSidebar/rightsidebar.module.css";
import { convertTo12Hour, extractHHMM, getOutletExtendedFlagForDate, scrollToBookingCard, stripTimeSelectionDayOffsetSuffix } from "../../../Utils/utils";
import TherapistDropdown from "../../DropDown/TherapistDropdown/TherapistDropdown";
import ServiceDropdown from "./ServiceDropdown";
import DropDownIcon from "../../Icons/DropDownIcon";
import TrashIcon from "../../Icons/TrashIcon";
import RoomDropdown from "../../DropDown/RoomDropdown/RoomDropdown";
import { UseTimeOptions } from "../../../Utils/Hooks/UseTimeOptions";
import ServiceAddons from "../../DropDown/ServiceAddons/ServiceAddons";
import SpecialRequestDropdown from "../../DropDown/SpecialRequestDropdown/SpecialRequestDropdown";
import { getBookingPreventChanges } from "../../../Utils/BookingPreventChanges";
import logger from "../../../Utils/logger";


const ServiceCard = ({
	service,
    segments = [],
	index,
	therapistOptions,
    therapistOptionsAll,
	handleTherapistSelect,
	handleRoomSelect,
	handleServiceRequestChange,
	removeService,
	selectedRooms,
	isSelfBooking = true,
	updateService,
	roomOptions,
	onReplaceService,
	showReplaceDropdown,
	setShowReplaceDropdown,
	replacingServiceKey,
	handleServiceReplace,
	onTimeClick,
	onPaymentInProgressAttempt,
	handleTherapistDropdownToggle,
	fromEdit,
    bookingId,
    onSplitDurationChange,
    handleRoomDropdownToggle,
	/** When false, hide next-day (+1) early-morning options for later services. */
	allowNextDayTimeOptions = true,
}) => {

const {
	selectedOutletTiming, 
	selectedDate, 
	selectedOutletData,
	setPendingTherapistSelection, 
	isEditBookingActive,
    editableBooking,
	isRoomFilterActive,
	homeRoomList,
	selectedOutlet,
	isMembershipApplied,
	publicHoliday,
} = useGlobalContext();

const [selectedTherapistOption, setSelectedTherapistOption] = useState(null);
const { preventChanges, isBookingPaymentInProgress} = getBookingPreventChanges(editableBooking);

const formatTherapistLabel = (option) => {
    if (!option) return null;
    return (
      <span className={`${styles.selectedTherapistLabel} d-flex align-center`}>
        <span
          className={`${styles.therapistCircle} ${
            option.gender === "female"
              ? styles.female
              : option.gender === "male"
              ? styles.male
              : ""
          }`}
          title={option.pager ? option.pager : 'N/A'}
        >
          {option.pager ? option.pager : 'N/A'}
        </span>
        <span title={option.label ? option.label : 'N/A'}>{option.label ? option.label : 'N/A'}</span>
      </span>
    );
  };
  
  useEffect(() => {
    setSelectedTherapistOption(formatTherapistLabel(service?.selected_therapist));
  }, [service?.selected_therapist]);

const isOutletExtended = getOutletExtendedFlagForDate(selectedOutletData?.timing, selectedDate);
const timeOptions = UseTimeOptions(selectedOutletTiming, selectedDate, isOutletExtended);

const getBaseSelectedDateString = () => {
    if (!selectedDate) return "";
    if (selectedDate instanceof Date) {
        return `${String(selectedDate.getDate()).padStart(2, "0")}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${selectedDate.getFullYear()}`;
    }
    const parsed = new Date(selectedDate);
    if (Number.isNaN(parsed.getTime())) return "";
    return `${String(parsed.getDate()).padStart(2, "0")}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${parsed.getFullYear()}`;
};

const getServiceDayOffset = (serviceDate) => {
    const baseDate = getBaseSelectedDateString();
    if (!baseDate || !serviceDate || baseDate === serviceDate) return 0;

    const [baseDay, baseMonth, baseYear] = baseDate.split("-").map(Number);
    const [serviceDay, serviceMonth, serviceYear] = String(serviceDate).split("-").map(Number);
    if (
        [baseDay, baseMonth, baseYear, serviceDay, serviceMonth, serviceYear].some((value) => Number.isNaN(value))
    ) {
        return 0;
    }

    const baseDateObj = new Date(baseYear, baseMonth - 1, baseDay);
    const serviceDateObj = new Date(serviceYear, serviceMonth - 1, serviceDay);
    baseDateObj.setHours(0, 0, 0, 0);
    serviceDateObj.setHours(0, 0, 0, 0);

    return Math.max(
        0,
        Math.round((serviceDateObj.getTime() - baseDateObj.getTime()) / (24 * 60 * 60 * 1000))
    );
};

const getTimeDropdownSelection = (serviceLike) => {
    const timeValue = serviceLike?.start_time;
    const serviceDate = serviceLike?.start_date;
    const normalizedTimeValue = typeof timeValue === "string" ? extractHHMM(timeValue) : "";
    const explicitDayOffset =
        typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)
            ? Math.max(0, serviceLike.dayOffset)
            : (typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)
                ? Math.max(0, Math.floor(serviceLike.absoluteMinutes / 1440))
                : getServiceDayOffset(serviceDate));
    const dayOffset = explicitDayOffset;
    const selectedValue = dayOffset > 0 ? `${normalizedTimeValue}+1` : normalizedTimeValue;
    const matchedOption = timeOptions.find((option) => option.value === selectedValue)
        || timeOptions.find((option) => {
            const optionTime = extractHHMM(stripTimeSelectionDayOffsetSuffix(String(option?.timeValue || option?.value || "")));
            return optionTime === normalizedTimeValue && Boolean(option?.isNextDay) === dayOffset > 0;
        })
        || timeOptions.find((option) => {
            const optionTime = extractHHMM(stripTimeSelectionDayOffsetSuffix(String(option?.timeValue || option?.value || "")));
            return optionTime === normalizedTimeValue;
        });

    return {
        selectedValue: matchedOption?.value || selectedValue,
        selectedLabel: matchedOption?.label
            || (dayOffset > 0
                ? `${convertTo12Hour(normalizedTimeValue)} (+${dayOffset})`
                : convertTo12Hour(normalizedTimeValue)),
    };
};

const displayTimeOptions = useMemo(() => {
    const options = Array.isArray(timeOptions) ? timeOptions : [];
    if (allowNextDayTimeOptions) return options;

    const selectedNextDayValues = new Set();
    [service, ...(Array.isArray(segments) ? segments : [])].forEach((svcLike) => {
        if (!svcLike) return;
        const selection = getTimeDropdownSelection(svcLike);
        if (String(selection?.selectedValue || "").endsWith("+1")) {
            selectedNextDayValues.add(String(selection.selectedValue));
        }
    });

    return options.filter((option) => {
        if (!option?.isNextDay && !String(option?.value || "").endsWith("+1")) return true;
        return selectedNextDayValues.has(String(option?.value));
    });
}, [timeOptions, allowNextDayTimeOptions, service, segments]);

const normalizeRoomBookingId = (value) => {
    if (value == null || value === "") return null;
    const normalized = Number(value);
    return Number.isNaN(normalized) ? null : normalized;
};

const resolvedRoomDisplay = useMemo(() => {
    const findHomeRoomItem = (roomId) => {
        if (roomId == null || !Array.isArray(homeRoomList)) return null;
        for (const room of homeRoomList) {
            if (!Array.isArray(room.items)) continue;
            const matchedItem = room.items.find((item) => Number(item.item_id) === Number(roomId));
            if (matchedItem) return matchedItem;
        }
        return null;
    };

    const toDisplayRoomName = (name) => {
        if (name == null || name === "") return null;
        if (typeof name === "number") return null;
        const trimmed = String(name).trim();
        if (!trimmed || /^\d+$/.test(trimmed)) return null;
        return trimmed;
    };

    const buildSelectedOption = (roomId, roomName, meta = {}) => {
        if (roomId == null && !roomName) {
            return {
                selectedOption: undefined,
                selectedLabel: "Room Selected",
            };
        }
        const itemName = roomName || "Room Selected";
        return {
            selectedOption: {
                roomid: roomId,
                item: {
                    item_id: roomId,
                    item_name: itemName,
                    room_type: meta.room_type ?? null,
                    facilities: Array.isArray(meta.facilities) ? meta.facilities : [],
                    item: meta.item_type ?? meta.item ?? null,
                },
            },
            selectedLabel: itemName,
        };
    };

    const selectedRoom = selectedRooms?.[service?.counterKey];
    const selectedRoomId = selectedRoom?.value ?? selectedRoom?.item_id ?? null;
    const rawServiceRoom = service?.room;
    const serviceRoomId = rawServiceRoom && typeof rawServiceRoom === "object"
        ? (rawServiceRoom.value ?? rawServiceRoom.item_id ?? rawServiceRoom.id ?? null)
        : (service?.roomId ?? rawServiceRoom ?? service?.room_segments?.[0]?.room_id ?? null);
    const serviceRoomName = toDisplayRoomName(
        service?.roomName ||
        service?.room_name ||
        (rawServiceRoom && typeof rawServiceRoom === "object"
            ? (rawServiceRoom.label || rawServiceRoom.item_name || null)
            : null)
    );
    const selectedRoomName = toDisplayRoomName(selectedRoom?.label || selectedRoom?.item_name);
    const serviceHasDifferentRoom =
        serviceRoomId != null &&
        selectedRoomId != null &&
        Number(serviceRoomId) !== Number(selectedRoomId);

    // After adding/selecting a service, selectedRooms is the live dropdown value.
    // Prefer it unless a calendar drag wrote a newer room onto the service.
    if (selectedRoom && !serviceHasDifferentRoom) {
        const matchedItem = findHomeRoomItem(selectedRoomId);
        return buildSelectedOption(
            selectedRoomId,
            selectedRoomName || serviceRoomName || matchedItem?.item_name || matchedItem?.name,
            {
                room_type: selectedRoom.room_type ?? matchedItem?.room_type,
                facilities: selectedRoom.facilities ?? matchedItem?.facilities,
                item_type: selectedRoom.item_type ?? selectedRoom.item ?? matchedItem?.item_type ?? matchedItem?.item,
            }
        );
    }

    if (serviceRoomId || serviceRoomName || isEditBookingActive || isRoomFilterActive) {
        let roomId = serviceRoomId;
        let roomName = serviceRoomName;
        if (!roomId && service.room_segments?.[0]?.room_id) {
            roomId = service.room_segments[0].room_id;
        }
        const matchedItem = findHomeRoomItem(roomId);
        if (!roomName) {
            roomName = toDisplayRoomName(matchedItem?.item_name || matchedItem?.name);
        }
        const seg0 = service?.room_segments?.[0];
        return buildSelectedOption(
            roomId,
            roomName,
            {
                room_type: matchedItem?.room_type ?? seg0?.room_type,
                facilities: matchedItem?.facilities ?? seg0?.facilities,
                item_type: matchedItem?.item_type ?? matchedItem?.item ?? seg0?.item_type,
            }
        );
    }

    return {
        selectedOption: undefined,
        selectedLabel: "Room Selected",
    };
}, [
    selectedRooms,
    service?.counterKey,
    service?.room,
    service?.roomId,
    service?.roomName,
    service?.room_name,
    service?.room_segments,
    homeRoomList,
    isEditBookingActive,
    isRoomFilterActive,
]);

const displayRoomOptions = useMemo(() => {
    if (!Array.isArray(roomOptions)) return [];
    if (!isEditBookingActive || !editableBooking?.id) return roomOptions;

    const currentBookingId = normalizeRoomBookingId(editableBooking.id);
    const currentBookingItemId = normalizeRoomBookingId(service?.booking_item_id);
    if (currentBookingItemId == null) {
        return roomOptions;
    }

    const isCurrentEditRoomBooking = (bookingLike) => {
        const bookingIds = [normalizeRoomBookingId(bookingLike?.booking_id),normalizeRoomBookingId(bookingLike?.bookingId),normalizeRoomBookingId(bookingLike?.booking?.id),normalizeRoomBookingId(bookingLike?.id),].filter((value) => value != null);
        const bookingItemIds = [normalizeRoomBookingId(bookingLike?.booking_item_id),normalizeRoomBookingId(bookingLike?.bookingItemId),normalizeRoomBookingId(bookingLike?.booking_item?.id),normalizeRoomBookingId(bookingLike?.item?.booking_item_id),normalizeRoomBookingId(bookingLike?.item?.id),].filter((value) => value != null);

        if (
            currentBookingItemId != null &&
            bookingItemIds.some((bookingItemId) => bookingItemId === currentBookingItemId)
        ) {
            return true;
        }

        if (
            currentBookingId != null &&
            bookingIds.some((bookingId) => bookingId === currentBookingId)
        ) {
            return true;
        }

        return false;
    };

    return roomOptions.map((option) => ({
        ...option,
        items: Array.isArray(option?.items)
            ? option.items.map((item) => ({
                ...item,
                bookings: Array.isArray(item?.bookings)
                    ? item.bookings.filter((booking) => !isCurrentEditRoomBooking(booking))
                    : [],
            }))
            : option?.items,
    }));
}, [roomOptions, isEditBookingActive, editableBooking?.id, service?.booking_item_id]);

	return (
		<div
			className={styles.selectedService}
			data-counter-key={service?.counterKey}
			data-booking-item-id={service?.booking_item_id || ""}
			onClick={() => {
				if (service.booking_item_id) {
					setTimeout(() => {
						scrollToBookingCard(service.booking_item_id,);
					}, 500);
				}
			}}
		>

            {preventChanges ? (
                <div className={`${styles.selectedServiceHead} d-flex justify-between align-start`}>
                    <div>
                        <div className={styles.serviceNameContainer}>
                            <h3 >{`${(service.groupBaseDuration ?? service.duration)} Mins ${service.name}`}</h3>
                        </div>
                    </div>
                </div>
            ):
            (
            <div className={`${styles.selectedServiceHead} d-flex justify-between align-start mb-0`}>
				<div>
					<div className={styles.serviceNameContainer}>
                        <h3 
							className={`${styles.clickableServiceName} d-flex align-center cursor-pointer`}
							onClick={(e) => {
								e.stopPropagation();
								onReplaceService(service.counterKey);
							}}
						>
                            {`${(service.groupBaseDuration ?? service.duration)} Mins ${service.name}`}
							<DropDownIcon 
								size={12} 
								color="#6b7280" 
								className={`${styles.dropdownIcon} ${showReplaceDropdown && replacingServiceKey === service.counterKey ? styles.rotated : ''}`}
							/>
						</h3>
						{/* Replace dropdown positioned below service name */}
						{showReplaceDropdown && replacingServiceKey === service.counterKey && (
							<div className={styles.replaceDropdownContainer}>
								<ServiceDropdown
									selectedService={service}
									onSelect={(newService) => {
										handleServiceReplace(newService);
									}}
									onClose={() => {
										setShowReplaceDropdown(false);
									}}
								/>
							</div>
						)}
					</div>
				</div>
				<div
					className={`${styles.delIconBtn} icon`}
					onClick={() => removeService(service.counterKey, isSelfBooking)}>
						<TrashIcon/>
				</div>
			</div>
            )}

			{/* ADD-ONS START  */}
			<div className={`${styles.dFlex} ${styles.spacing} position-relative`}>
				<label className={styles.label}>Add-ons:</label>
				<ServiceAddons
					serviceId={
						service?.id ??
						service?.service ??
						service?.service_id ??
						null
					}
					outletId={selectedOutlet}
					isMembershipApplied={isMembershipApplied}
					publicHoliday={publicHoliday}
					selectedDate={selectedDate}
					serviceStartDate={service?.start_date}
					serviceStartTime={service?.start_time}
					selectedValues={service.addons || []}
					readOnly={preventChanges}
					onChange={
						preventChanges
							? undefined
							: (selectedAddons) => {
									// Persist selected add-ons on the service itself
									// Consumers can decide how to use this data downstream
									updateService(service.counterKey, {
										addons: selectedAddons,
									});
							  }
					}
				/>
			</div>
			{/* ADD-ONS END  */}
			
			<div className={styles.innerServiceDropdowns}>
				{/* Therapist Dropdown */}
				<div className={`${styles.dFlex} ${styles.justifyBetween}`}>
					<div className={`${styles.therapistWith} ${styles.dFlex} align-center`}>
						<label className={styles.label}>With:</label>
                        <TherapistDropdown
							options={therapistOptions}
                            selectedLabel={selectedTherapistOption}
							placeholder="Select therapist"
							onSelect={(therapist) => {
								const normalized = typeof therapist === 'string'
									? { value: null, label: therapist }
									: (therapist ?? { value: null, label: therapistOptions?.find?.((t) => t.value === null)?.label || 'Select therapist' });
								setPendingTherapistSelection(normalized);
								handleTherapistSelect(
									service,
									service.counterKey,
									normalized
								);
							}}
							onDropdownToggle={(isOpen) => {
								if (isOpen && handleTherapistDropdownToggle) {
									handleTherapistDropdownToggle(service, isOpen);
								}
							}}
							dropdownClass={`${styles.dropdownNoBg} dropdownNoBg`}
							showGenderCircle
						/>
					</div>

					{/* Requested Therapist Checkbox */}
					{service.therapist !== undefined &&
						service.therapist !== "" &&
						service.therapist !== null && (
							<div className={styles.requestedCheckbox}>
								<label className={styles.checkbox}>
									<input
										type="checkbox"
										checked={service.requested_person === 1}
										disabled={service.requested_therapist_disabled}
										onChange={e => {
											const checked = e.target.checked;
											// Update service state - use functional update pattern to ensure we have latest state
											updateService(service.counterKey, { requested_person: checked ? 1 : 0 });
											// Note: Removed handleTherapistDropdownToggle call from here as it's already handled
											// by the TherapistDropdown component's onDropdownToggle prop
											// Emit requested flag override so icon updates during edit
											try {
												if (fromEdit && bookingId && service?.booking_item_id) {
													window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
														detail: {
															bookingId,
															itemId: service.booking_item_id,
															isRequested: !!checked,
														}
													}));
												}
											} catch {}
										}}
										className={styles.hiddenCheckbox}
									/>
									<span className={`${styles.checkmark} icn check`}/>
									<img src={RequestedIconCheck} alt="icon" />
									<span className={styles.labelText}>
										Requested therapist
									</span>
								</label>
							</div>
						)}
				</div>

				{/* Error message */}
				{(
					// !pendingTherapistSelection?.label ||
					(!service?.selected_therapist)
				) && (
					<p className={styles.errorMessage}  style={{ marginTop: "-10px", marginBottom: "20px" }}>Please select a therapist</p>
				)}

				{/* Room Dropdown */}
                <div className="d-flex align-center justify-between">
					<div className={styles.dFlex}>
						<label className={styles.label}>Using:</label>
						<RoomDropdown
							options={displayRoomOptions}
							onSelect={(selected) => {
								// Transform RoomDropdown format to ServiceSelector expected format
								const facilities = Array.isArray(selected?.facilities)
									? selected.facilities
									: [];
								const roomObject = {
									value: selected?.item_id,
									label: selected?.item_name || "Select room",
									room_type: selected?.room_type ?? null,
									facilities,
									item_type: selected?.item_type ?? selected?.item ?? null,
								};
								handleRoomSelect(service.counterKey, roomObject);
								// Note: handleRoomSelect already handles room data updates and calendar sync
								// No need to call handleTherapistSelect as it would reset therapist data
							}}
							
							selectedOption={resolvedRoomDisplay.selectedOption}
							selectedLabel={
								<div className="d-flex align-center">
									{resolvedRoomDisplay.selectedLabel}
								</div>
							}
							onDropdownToggle={(isOpen) => {
								if (isOpen && handleRoomDropdownToggle) {
									handleRoomDropdownToggle(service, isOpen);
								}
							}}
						/>
					</div>

                    <div className={styles.requestedCheckbox}>
                        <label className={styles.checkbox}>
                            <input
                                type="checkbox"
                                // checked={service.room_segments && service.room_segments.length > 0 && service.room_segments[0].requested_room === 1}
                                checked={service.requested_room === 1 || service?.requested_room === true}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    const updatePayload = { 
                                        requested_room: checked ? 1 : 0
                                    };
                                    
                                    // Only update room_segments if they exist
                                    if (service.room_segments && Array.isArray(service.room_segments) && service.room_segments.length > 0) {
                                        updatePayload.room_segments = service.room_segments.map(segment => ({ ...segment, requested_room: checked ? 1 : 0 }));
                                    }
                                    
                                    updateService(service.counterKey, updatePayload);
                                    
                                    // Emit requested_room flag override so icon updates during edit
                                    try {
                                        if (fromEdit && bookingId && service?.booking_item_id) {
                                            window.dispatchEvent(new CustomEvent('editOverrideUpdate', {
                                                detail: {
                                                    bookingId,
                                                    itemId: service.booking_item_id,
                                                    hasRequestedRoom: !!checked,
                                                }
                                            }));
                                        }
                                    } catch {}
                                }}
                                className={styles.hiddenCheckbox}
                            />
                            <span className={`${styles.checkmark} icn check`}/>
                            <img src={RequestedRoomIconCheck} alt="icon" />
                            <span className={styles.labelText}>
                                Requested room
                            </span>
                        </label>
                    </div>
                </div>
				
				{(!selectedRooms[service.counterKey]?.label && !service.room_name && !service.room_segments) && (
					<p className={styles.errorMessage} style={{ marginTop: "-10px", marginBottom: "20px" }}>No room selected</p>
				)}

				<div className={styles.dFlex}>
					<label className={styles.label}>Request:</label>
					<SpecialRequestDropdown
						key={`${service.counterKey}_${index}`}
						defaultSelected={
							Array.isArray(service.service_request)
								? service.service_request
								: typeof service.service_request === "string" &&
								  service.service_request.length > 0
								? service.service_request.split(",")
								: []
						}
						onChange={(selected) =>
							handleServiceRequestChange(
								selected,
								service.counterKey
							)
						}
						dropdownClass={`dropdownNoBg ${styles.requestType}`}
					/>
				</div>

				<div className={`${styles.dFlex} ${styles.spacing}`}>
					<div className={`${styles.dFlex}`}>
						<label className={styles.label}>For:</label>
                    {service.isSplitSegment ? (
                            <>
                            <input
                                type="number"
                                className={`${styles.groupiconFormControl} ${styles.noSpinner}`}
                                style={{ width: 35, marginLeft: 0, paddingLeft: 0 }}
                                value={(service.duration === null || service.duration === undefined || service.duration === '') ? '' : service.duration}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const val = raw === '' ? null : Number(raw);
                                    let nextEnd;
                                    const duration = val == null || Number.isNaN(val) ? 0 : val;
                                    
                                    // Always calculate end_time based on start_time and duration
                                    const [h, m] = String(service.start_time || "00:00").split(":").map((n) => parseInt(n, 10));
                                    const total = h * 60 + m + duration;
                                    const nh = Math.floor(((total % (24 * 60)) + (24 * 60)) % (24 * 60) / 60);
                                    const nm = ((total % 60) + 60) % 60;
                                    nextEnd = `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
                                    
                                    const payload = {
                                        duration: val == null ? null : val,
                                        end_time: nextEnd
                                    };
                                    
                                    // Update room_segments if they exist - ensure end_time matches service end_time
                                    if (service.room_segments && service.room_segments.length > 0) {
                                        const startTime24h = String(service.start_time || "00:00");
                                        payload.room_segments = service.room_segments.map(segment => ({
                                            ...segment,
                                            start_time: startTime24h + ":00",
                                            duration: duration,
                                            end_time: nextEnd + ":00"
                                        }));
                                    }
                                    
                                    updateService(service.counterKey, payload);
                                }}
                            />
                            <span className={styles.sameFont}>mins</span>
                            </>
                        ) : (
						<p className={styles.sameFont}>{service.duration} mins</p>
                        )}
					</div>
					<div className={`${styles.dFlex} ${styles.space}`}>
						<label className={styles.label}>At:</label>
						<div
							className={styles.timeDropdownWrapper}
							onClickCapture={() => {
								if (isBookingPaymentInProgress) {
									onPaymentInProgressAttempt?.();
								}
							}}
						>
							{(() => {
								const { selectedLabel, selectedValue } = getTimeDropdownSelection(
									service
								);
								return (
							<DropdownNoLabel
								options={displayTimeOptions}
								selectedLabel={<div className="d-flex align-center">{selectedLabel}</div>}
								selectedValue={selectedValue}
								onSelect={(timeOption) => {
									if (onTimeClick) {
										onTimeClick(
											service,
											service.counterKey, 
											timeOption
										);
									}
								}}
								dropdownClass={`${styles.dropdownNoBg} dropdownNoBg`}
								dropdownMenuClass={styles.serviceCardTimeList}
								placeholder="Select Time"
								placeholderClass="greyPlaceholder"
								// disabled={isBookingPaymentInProgress}
							/>
								);
							})()}
						</div>
					</div>
				</div>

				                {/* Render split segments under the main For/At and above Using */}
								{Array.isArray(segments) && segments.length > 1 && segments
                    .filter((seg) => seg.counterKey !== service.counterKey)
                    .map((seg) => {
                        const segSelectedTherapist = (() => {
                            const currentId = typeof seg?.therapist === 'object' && seg?.therapist !== null
                                ? seg.therapist.value ?? seg.therapist.id
                                : seg?.therapist;
                            const opts = therapistOptionsAll?.[seg.counterKey] || therapistOptions || [];
                            const found = opts && opts.length > 0
                                ? opts.find((t) => String(t.value) === String(currentId))
                                : null;
                            return found?.label || seg?.therapist_name || 'Select therapist';
                        })();
                        
                        // Check if therapist is selected for this segment
                        const isTherapistSelected = (() => {
                            const currentId = typeof seg?.therapist === 'object' && seg?.therapist !== null
                                ? seg.therapist.value ?? seg.therapist.id
                                : seg?.therapist;
                            return currentId != null && currentId !== '';
                        })();
                        return (
                            <div key={`seg_${seg.counterKey}`} className={styles.innerServiceDropdowns}>
                                <div className={`${styles.dFlex} ${styles.justifyBetween}`}>
                                    <div className={`${styles.therapistWith} ${styles.dFlex} align-center`}>
                                        <label className={styles.label}>With:</label>
                                        <TherapistDropdown
                                            options={therapistOptionsAll?.[seg.counterKey] || therapistOptions}
                                            selectedLabel={segSelectedTherapist}
                                            onSelect={(therapist) => {
                                                const normalized = typeof therapist === 'string'
                                                    ? { value: null, label: therapist }
                                                    : (therapist ?? { value: null, label: 'Select therapist' });
                                                handleTherapistSelect(seg, seg.counterKey, normalized);
                                            }}
                                            onDropdownToggle={(isOpen) => {
                                                if (isOpen && handleTherapistDropdownToggle) {
                                                    handleTherapistDropdownToggle(seg, isOpen);
                                                }
                                            }}
                                            dropdownClass={`${styles.dropdownNoBg} dropdownNoBg`}
                                            showGenderCircle
                                        />
                                    </div>
                                    <div className={`${styles.delIconBtn} icon`} onClick={() => removeService(seg.counterKey, isSelfBooking)}>
                                        <TrashIcon/>
                                    </div>
                                </div>
                                 {/* Show error message if no therapist selected */}
                                 {!isTherapistSelected && (
                                     <p className={styles.errorMessage} style={{ marginTop: -15, marginBottom: 8 }}>
                                         Please select a therapist
                                     </p>
                                 )}
                                <div className={`${styles.dFlex} ${styles.spacing}`}>
                                    <div className={`${styles.dFlex}`}>
                                        <label className={styles.label}>For:</label>
                                        <>
                                        <input
                                            type="number"
                                            min="5"
                                            step="5"
                                            className={`${styles.groupiconFormControl} ${styles.noSpinner} ${styles.splitDuration}`}
                                            style={{ width: 35, marginLeft: 0 ,paddingLeft: 0 }}
                                            value={(seg.duration === null || seg.duration === undefined || seg.duration === '') ? '' : seg.duration}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const val = raw === '' ? null : Number(raw);
                                                let nextEnd;
                                                const duration = val == null || Number.isNaN(val) ? 0 : val;
                                                
                                                // Always calculate end_time based on start_time and duration
                                                const [h, m] = String(seg.start_time || '00:00').split(':').map((n) => parseInt(n, 10));
                                                const total = h * 60 + m + duration;
                                                const nh = Math.floor(((total % (24 * 60)) + (24 * 60)) % (24 * 60) / 60);
                                                const nm = ((total % 60) + 60) % 60;
                                                nextEnd = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
                                                
                                                const payload = {
                                                    duration: val == null ? null : val,
                                                    end_time: nextEnd
                                                };
                                                
                                                // Update room_segments if they exist - ensure end_time matches service end_time
                                                if (seg.room_segments && seg.room_segments.length > 0) {
                                                    const startTime24h = String(seg.start_time || '00:00');
                                                    payload.room_segments = seg.room_segments.map(segment => ({
                                                        ...segment,
                                                        start_time: startTime24h + ":00",
                                                        duration: duration,
                                                        end_time: nextEnd + ":00"
                                                    }));
                                                }
                                                
                                                updateService(seg.counterKey, payload);
                                                if (typeof onSplitDurationChange === 'function') {
                                                    try { onSplitDurationChange(seg.counterKey, val); } catch {}
                                                }
                                            }}
                                        />
                                        <span className={styles.sameFont}>mins</span>
                                        </>
                                    </div>
                                    <div className={`${styles.dFlex} ${styles.space}`}>
                                        <label className={styles.label}>At:</label>
                                        <div
                                            className={styles.timeDropdownWrapper}
                                            onClickCapture={() => {
                                                if (isBookingPaymentInProgress) {
                                                    onPaymentInProgressAttempt?.();
                                                }
                                            }}
                                        >
                                            {(() => {
                                                const { selectedLabel, selectedValue } = getTimeDropdownSelection(
                                                    seg
                                                );
                                                return (
                                            <DropdownNoLabel
                                                options={displayTimeOptions}
                                                selectedLabel={<div className="d-flex align-center">{selectedLabel}</div>}
                                                selectedValue={selectedValue}
                                                onSelect={(timeOption) => {
                                                    if (onTimeClick) onTimeClick(seg, seg.counterKey, timeOption);
                                                }}
                                                dropdownClass={`${styles.dropdownNoBg} dropdownNoBg`}
                                                dropdownMenuClass={styles.serviceCardTimeList}
                                                placeholder="Select Time"
                                                placeholderClass="greyPlaceholder"
                                                disabled={isBookingPaymentInProgress}
                                            />
                                                );
                                            })()}
                                        </div>
                                    </div>
                                 </div>
                                 
                                 {/* Check if duration and time are properly selected - show below For/At dropdowns */}
                                 {(() => {
                                     const duration = Number(seg.duration || 0);
                                     const hasValidTime = seg.start_time && seg.start_time !== '00:00' && seg.start_time !== '';
                                     const needsDurationTime = duration === 0 || !hasValidTime;
                                     return needsDurationTime ? (
                                         <p className={styles.errorMessage} style={{ marginTop: -7, marginBottom: 8 }}>
                                             Please enter duration
                                         </p>
                                     ) : null;
                                 })()}
                                 
                            </div>
                        );
                    })}



                {(() => {
                    const baseDur = Number(service.groupBaseDuration ?? service.duration ?? 0);
                    const segs = (segments || []).filter(s => s.counterKey !== service.counterKey);
                    const totalDur = Number(service.duration || 0) + segs.reduce((acc, s) => acc + Number(s.duration || 0), 0);
                    const tooMuch = baseDur > 0 && totalDur > baseDur;
                    return tooMuch ? (
                        <p className={styles.errorMessage} style={{ marginTop: 6 }}>Total service time needs to add up to {baseDur} Mins</p>
                    ) : null;
                })()}
			</div>
		</div>
	);
};

export default ServiceCard;